/**
 * Thin seam over the Firebase SDK for web push.
 *
 * Init is from INJECTED config only (each app resolves its own per-env
 * Firebase Web App entry per ADR-132 Amendment 4 and gates on it failing
 * LOUD app-side before calling in here) — the lib never reads `window.__ENV`
 * and never carries a config of its own, so no product can piggyback on
 * another product's Firebase entry/config through the shared code
 * (ADR-132 Amendment 4).
 */
import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';

/** Firebase web app config shape (public by design — not a secret). */
export interface WebPushFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

let cachedApp: FirebaseApp | null = null;

/**
 * Idempotent Firebase app init from injected config. Reuses the SDK's
 * default app when one already exists (each product runs alone in its own
 * page, so the default-app slot is unambiguous).
 */
export function getWebPushFirebaseApp(
  config: WebPushFirebaseConfig,
): FirebaseApp {
  if (cachedApp) return cachedApp;
  cachedApp = getApps().length ? getApp() : initializeApp(config);
  return cachedApp;
}

export interface RequestFcmRegistrationTokenOptions {
  app: FirebaseApp;
  /** Firebase project Web Push certificate public key (VAPID). */
  vapidKey: string;
  /**
   * Explicit SW registration when the page owns which SW receives pushes;
   * omitted, the SDK registers its default `firebase-messaging-sw.js`.
   */
  serviceWorkerRegistration?: ServiceWorkerRegistration;
}

/**
 * Mint (or fetch the cached) FCM registration token for this browser.
 * Surfaces the browser's notification-permission prompt when permission is
 * still `default`. Returns `null` for an empty token; SDK errors (e.g.
 * `messaging/permission-blocked`) propagate — each app keeps its own
 * catch/log semantics.
 */
export async function requestFcmRegistrationToken(
  options: RequestFcmRegistrationTokenOptions,
): Promise<string | null> {
  const messaging = getMessaging(options.app);
  const token = await getToken(messaging, {
    vapidKey: options.vapidKey,
    ...(options.serviceWorkerRegistration
      ? { serviceWorkerRegistration: options.serviceWorkerRegistration }
      : {}),
  });
  return token || null;
}
